/**
 * Open project tasks for a case, or for the signed-in person.
 * `GET /projects/tasks` ANDs `case_id` and `owner`, so the case block reads both and unions them.
 * A free task is the only one with a checkbox. A completion rule renders the server's "completes when …" line.
 */
import { SkeletonRows } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { describeError } from '../../lib/api/client'
import type { TaskOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { officeCalendarDay } from '../../lib/toronto-time'
import { projectsApi, projectTasksQuery } from './api'
import { projectReadAbsent } from './project-mark'

export function officeToday(now = new Date()): string {
  return officeCalendarDay(now)
}

export function taskDueDay(task: TaskOut): string | null {
  if (!task.due_on) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(task.due_on)) return task.due_on
  const parsed = new Date(task.due_on)
  if (Number.isNaN(parsed.getTime())) return null
  return officeToday(parsed)
}

export function taskIsOverdue(task: TaskOut, today: string): boolean {
  const due = taskDueDay(task)
  return Boolean(due && task.state === 'open' && due < today)
}

export function taskIsFree(task: TaskOut): boolean {
  return Boolean(task.can_tick) && !task.completion_rule && !task.completes_when
}

function ruleText(task: TaskOut): string | null {
  if (taskIsFree(task)) return null
  if (task.completes_when) return task.completes_when
  if (task.completion_rule) return 'Completes from the linked record'
  return null
}

function dueLabel(task: TaskOut, today: string): string | null {
  const due = taskDueDay(task)
  if (!due) return null
  if (taskIsOverdue(task, today)) return 'overdue'
  return formatWhen(`${due}T12:00:00Z`, { style: 'date' })
}

function mergeTask(left: TaskOut, right: TaskOut): TaskOut {
  const completes = left.completes_when || right.completes_when || null
  const rule = left.completion_rule ?? right.completion_rule ?? null
  return {
    ...left,
    ...right,
    completes_when: completes,
    completion_rule: rule,
    can_tick: Boolean(left.can_tick && right.can_tick && !completes && !rule),
  }
}

export function unionTasks(groups: Array<TaskOut[] | undefined>): TaskOut[] {
  const byId = new Map<string, TaskOut>()
  for (const group of groups) {
    for (const task of group ?? []) {
      const current = byId.get(task.id)
      byId.set(task.id, current ? mergeTask(current, task) : task)
    }
  }
  return [...byId.values()]
}

export function compareTasks(a: TaskOut, b: TaskOut, today = officeToday()): number {
  const rank = Number(taskIsOverdue(b, today)) - Number(taskIsOverdue(a, today))
  if (rank) return rank
  const ad = taskDueDay(a) ?? '9999-99-99'
  const bd = taskDueDay(b) ?? '9999-99-99'
  if (ad !== bd) return ad < bd ? -1 : 1
  const ap = a.project_number ?? ''
  const bp = b.project_number ?? ''
  if (ap !== bp) return ap < bp ? -1 : 1
  return a.text.localeCompare(b.text)
}

export function useTickTasks() {
  const queryClient = useQueryClient()
  return useMutation({
    retry: 0,
    mutationFn: (task: TaskOut) => projectsApi.tickTask({ path: { task_id: task.id } }),
    onMutate: async (task) => {
      await queryClient.cancelQueries({ queryKey: ['projects', 'tasks'] })
      const snapshots = queryClient.getQueriesData<TaskOut[]>({ queryKey: ['projects', 'tasks'] })
      queryClient.setQueriesData<TaskOut[]>({ queryKey: ['projects', 'tasks'] }, (current) =>
        current?.map((row) => (row.id === task.id ? { ...row, state: 'done' } : row)),
      )
      return { snapshots }
    },
    onError: (_error, _task, context) => {
      for (const [key, data] of context?.snapshots ?? []) queryClient.setQueryData(key, data)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects', 'tasks'] })
    },
  })
}

export function TaskTickRow({ task, tick }: { task: TaskOut; tick: ReturnType<typeof useTickTasks> }) {
  const today = officeToday()
  const free = taskIsFree(task)
  const pending = tick.isPending && tick.variables?.id === task.id
  const rule = ruleText(task)
  const due = dueLabel(task, today)
  return (
    <li className="flex items-start gap-2 py-1.5">
      {free ? (
        <input
          type="checkbox"
          className="mt-1 size-4 shrink-0"
          aria-label={`Mark done: ${task.text}`}
          checked={task.state === 'done'}
          disabled={pending || task.state === 'done'}
          onChange={() => {
            if (task.state !== 'done' && !pending) tick.mutate(task)
          }}
        />
      ) : (
        <span className="mt-1 inline-block size-4 shrink-0" aria-hidden="true" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-sm">{task.text}</span>
        {task.project_number ? <span className="block text-xs text-muted-foreground">{task.project_number}</span> : null}
        {rule ? <span className="block text-xs text-muted-foreground">{rule}</span> : null}
        {due ? <span className="block text-xs text-muted-foreground">{due}</span> : null}
      </span>
    </li>
  )
}

export function CaseTaskList({ caseId }: { caseId: string }) {
  const byCase = useQuery(projectTasksQuery({ case_id: caseId, state: 'open' }))
  const mine = useQuery(projectTasksQuery({ owner: 'me', state: 'open' }))
  const tick = useTickTasks()
  const failed = [byCase, mine].filter((query) => query.isError && !projectReadAbsent(query.error))
  const loading = (byCase.isPending && !byCase.data) || (mine.isPending && !mine.data)
  const today = officeToday()
  const tasks = unionTasks([byCase.data, mine.data]).sort((a, b) => compareTasks(a, b, today))

  return (
    <section aria-label="Project tasks" data-section="project-tasks" className="space-y-2">
      <h3 className="text-sm font-semibold">Project tasks</h3>
      {failed.length ? (
        <p role="status" className="text-sm text-warning">
          <span>Projects unreachable; this task list may be incomplete.</span>{' '}
          <button
            type="button"
            className="cursor-pointer underline"
            onClick={() => {
              void byCase.refetch()
              void mine.refetch()
            }}
          >
            Retry
          </button>
        </p>
      ) : null}
      {loading ? (
        <SkeletonRows count={2} />
      ) : tasks.length === 0 && failed.length === 0 ? (
        <p className="text-sm text-muted-foreground">No open project tasks.</p>
      ) : (
        <ul className="divide-y divide-border">
          {tasks.map((task) => (
            <TaskTickRow key={task.id} task={task} tick={tick} />
          ))}
        </ul>
      )}
      {tick.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {describeError(tick.error)}
        </p>
      ) : null}
    </section>
  )
}
